import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ExternalLink, Eye, Calendar, FileText, ArrowRight, BookMarked } from "lucide-react";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { books } from "@/data/books";

const BookCard = ({ book, index, isVisible }) => {
  const [imageError, setImageError] = useState(false);

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, delay: index * 0.1, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      className="group relative bg-white dark:bg-dark-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
      whileHover={{ y: -6 }}
    >
      {/* Clickable overlay to book detail page */}
      <Link to={`/books/${book.id}`} className="absolute inset-0 z-10" aria-label={`View ${book.title}`} />
      {/* Cover */}
      <div className="relative h-72 overflow-hidden flex items-center justify-center bg-gray-900">
        {book.cover && !imageError ? (
          <img
            src={book.cover}
            alt={book.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          /* Styled book cover with title */
          <div className="w-full h-full flex items-stretch">
            {/* Spine */}
            <div className="w-6 bg-emerald-700 flex-shrink-0" />
            {/* Front cover */}
            <div className="flex-1 bg-gray-900 flex flex-col justify-between p-6 relative overflow-hidden">
              {/* Decorative lines */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 right-0 h-px bg-emerald-400" />
                <div className="absolute bottom-12 left-0 right-0 h-px bg-emerald-400" />
              </div>
              {/* Top: genre tag */}
              <span className="text-emerald-400 text-xs font-semibold uppercase tracking-widest">
                {book.genre || "Book"}
              </span>
              {/* Middle: title */}
              <div>
                <h4 className="text-white text-xl font-bold leading-snug mb-2">
                  {book.title}
                </h4>
                {book.subtitle && (
                  <p className="text-gray-400 text-xs italic">{book.subtitle}</p>
                )}
              </div>
              {/* Bottom: author */}
              <p className="text-gray-500 text-xs tracking-wide">
                Boye Emmanuel Tete
              </p>
            </div>
          </div>
        )}

        {book.featured && (
          <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10">
            Featured
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 line-clamp-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
          {book.title}
        </h3>

        {book.subtitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-1 italic">
            {book.subtitle}
          </p>
        )}

        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 mb-4">
          {book.description}
        </p>

        {/* Meta */}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400 mb-4">
          {book.publishedDate && (
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(book.publishedDate).getFullYear()}
            </span>
          )}
          {book.pages && (
            <span className="flex items-center gap-1">
              <FileText size={12} />
              {book.pages} pages
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {book.pdfUrl && (
            <a
              href={book.pdfUrl}
              download
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <FileText size={14} />
              Download PDF
            </a>
          )}
          {book.readUrl && (
            <a
              href={book.readUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 border border-emerald-500/40 hover:border-emerald-400 hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <BookMarked size={14} />
              Read
            </a>
          )}
          {book.purchaseUrl && (
            <a
              href={book.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={14} />
              Get Book
            </a>
          )}
          {book.previewUrl && (
            <a
              href={book.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-700 hover:border-emerald-400 dark:hover:border-emerald-500 text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Eye size={14} />
              Preview
            </a>
          )}
          {!book.purchaseUrl && !book.previewUrl && !book.pdfUrl && (
            <span className="flex-1 text-center text-sm text-gray-400 dark:text-gray-500 py-2">
              Coming Soon
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const BooksSection = () => {
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.05,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
  });

  const isEmpty = books.length === 0;

  return (
    <section
      id="books"
      ref={elementRef}
      className="py-20 bg-gray-50 dark:bg-dark-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isIntersecting ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-emerald-500" />
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-sm uppercase tracking-widest">
              Books
            </span>
            <div className="h-px w-12 bg-emerald-500" />
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            My{" "}
            <span className="text-emerald-500">
              Books
            </span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            Books I've written and published — from technical guides to
            personal development.
          </p>
        </motion.div>

        {/* Grid or Empty State */}
        {isEmpty ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isIntersecting ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center py-24 text-center"
          >
            <div className="w-24 h-24 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-6">
              <BookOpen size={40} className="text-emerald-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Books Coming Soon
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md">
              I'm currently working on my first books. Check back soon — or
              reach out if you'd like to be notified when they launch.
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {books.map((book, index) => (
              <BookCard
                key={book.id}
                book={book}
                index={index}
                isVisible={isIntersecting}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BooksSection;
